import {hideNotification, showNotification, useTranslationContext} from 'platform/components';

import {useHttpCalls} from '../api/useHttpCalls';
import {useDataGridContext} from '../context/useDataGridContext';
import {selectActivePreset, selectGridCode, type EagGridStore} from '../store/createEagGridStore';
import {useEagGridSelector} from '../store/useEagGridSelector';
import {useEagGridStore} from '../store/useEagGridStore';
import type {GetDataQueryResponse, Preset, VirtualPreset} from '../types/Api';
import type {UsePresetStateReturn} from '../types/UsePresetStateReturn';
import {buildFilterValuesFromDataQuery} from '../utils/buildFilterValuesFromDataQuery';
import {filterTechnicalArray} from '../utils/filterTechnicalArray';
import {isPresetCustom} from '../utils/isPresetCustom';
import {objectHasEveryEqualPropOfAnother} from '../utils/objectHasEveryEqualPropOfAnother';
import {reconcilePresetWithQueryModifier} from '../utils/reconcilePresetWithQueryModifier';
import {setSearchParamsByGridCode} from '../utils/url/setSearchParamsByGridCode';
import {applyPresetQuerySync} from './applyPresetQuerySync';

type UsePresetActivationReturn = Pick<
  UsePresetStateReturn,
  'activatePreset' | 'deactivatePreset' | 'applyPresetToVirtual' | 'resetOverrides'
>;

const pendingCopyActivation = new WeakMap<EagGridStore, Promise<void>>();
const gridsWithCopyActivationWarning = new WeakSet<EagGridStore>();

export const usePresetActivation = (): UsePresetActivationReturn => {
  const t = useTranslationContext();
  const httpCalls = useHttpCalls();
  const gridContext = useDataGridContext();
  const store = useEagGridStore();
  const gridCode = useEagGridSelector(selectGridCode);
  const activePreset = useEagGridSelector(selectActivePreset)!;
  const copyActivationWarningId = `copy-to-default-activation-${gridCode}`;

  const clearCopyActivationWarning = () => {
    if (gridsWithCopyActivationWarning.has(store)) {
      gridsWithCopyActivationWarning.delete(store);
      hideNotification(copyActivationWarningId);
    }
  };

  const activateVirtualPreset = async (
    virtualPreset: VirtualPreset,
    dataQuery: GetDataQueryResponse,
    isCopy = false
  ): Promise<void> => {
    if (isCopy) {
      await pendingCopyActivation.get(store);
      try {
        await httpCalls.activatePresetForCopy();
      } catch {
        showNotification.warning(t('page.datagrid.copyToDefault.activationWarning'), {
          id: copyActivationWarningId,
          autoClose: false,
        });
        gridsWithCopyActivationWarning.add(store);
        const retry = httpCalls.activatePresetForCopy();
        pendingCopyActivation.set(
          store,
          retry
            .then(
              () => undefined,
              () => undefined
            )
            .finally(() => pendingCopyActivation.delete(store))
        );
        await retry;
        clearCopyActivationWarning();
      }
    } else {
      await pendingCopyActivation.get(store);
      await httpCalls.activatePreset(null);
      clearCopyActivationWarning();
    }

    applyPresetQuerySync(store, gridCode, virtualPreset.dataQueryId, virtualPreset);
    store.setActivePresetDataQuery(dataQuery);
    store.setDataQuery(dataQuery);
    store.setFilterValues(buildFilterValuesFromDataQuery(dataQuery));
    store.setActivePreset(virtualPreset);
    setSearchParamsByGridCode(gridCode, virtualPreset.dataQueryId);
    store.setQueryId(virtualPreset.dataQueryId);
    store.setIsPresetChanged(false);
  };

  const activatePreset = async (preset: Preset): Promise<void> => {
    const presetDetail = await httpCalls.getPreset(preset.id);

    if (!presetDetail) {
      return;
    }

    // Sync queryId/URL from the DETAIL's dataQueryId, never from the list item's.
    // The panel's customPresets list can hold a stale dataQueryId: background
    // queryModifier reconciliation (GridLifecycle load) PUTs presets to a freshly
    // minted dataQueryId without refreshing the list. Using the stale id here makes
    // the dirty check compare saved (detail, new id) vs current (queryId, old id)
    // and render a permanent spurious "* " right after activation (T20-134447).
    const dataQuery = await httpCalls.getDataQuery(presetDetail.dataQueryId);

    if (!dataQuery) {
      return;
    }

    applyPresetQuerySync(store, gridCode, presetDetail.dataQueryId, presetDetail);
    store.setActivePresetDataQuery(dataQuery);

    await pendingCopyActivation.get(store);
    await httpCalls.activatePreset(preset.id).then(() => {
      clearCopyActivationWarning();
      store.setDataQuery(dataQuery);
      // Seed filterValues SYNCHRONOUSLY from the activated preset's data query.
      // useFilterHydration re-seeds them too, but only in a React effect — the
      // engine's fetch triggered by setDataQuery can execute before that effect,
      // read the PREVIOUS preset's filterValues, and (when it has to mint a fresh
      // query, e.g. a queryModifier static filter is missing) bake the old
      // preset's filters into the new query (T20-134468 filter leak).
      store.setFilterValues(buildFilterValuesFromDataQuery(dataQuery));
      store.setActivePreset(presetDetail);
      setSearchParamsByGridCode(gridCode, presetDetail.dataQueryId);
      store.setQueryId(presetDetail.dataQueryId);
      store.setIsPresetChanged(false);
    });
  };

  const deactivatePreset = async (): Promise<void> => {
    // A GET cached just before the copy could still hold the old Default state.
    const newVirtualPreset = await httpCalls.getVirtualPreset({fresh: true});

    if (!newVirtualPreset) {
      return;
    }

    const dataQuery = await httpCalls.getDataQuery(newVirtualPreset.dataQueryId);

    if (!dataQuery) {
      return;
    }

    const virtualPresetAsActive: VirtualPreset = {
      columnsSettings: newVirtualPreset.columnsSettings,
      dataQueryId: newVirtualPreset.dataQueryId,
      gridSettings: newVirtualPreset.gridSettings,
    };

    store.setVirtualPreset(virtualPresetAsActive);
    await activateVirtualPreset(virtualPresetAsActive, dataQuery);
  };

  const applyPresetToVirtual = async (presetId: string): Promise<void> => {
    await httpCalls.beginVirtualPresetCopy();
    try {
      const presetDetail = await httpCalls.getPreset(presetId, {fresh: true, forCopy: true});
      if (!presetDetail) {
        throw new Error('Unable to load the custom preset');
      }

      const staticFilters = gridContext?.queryModifier?.({});
      const reconciled = staticFilters
        ? await reconcilePresetWithQueryModifier(
            presetDetail,
            httpCalls,
            staticFilters,
            undefined,
            {
              updateSourcePreset: false,
            }
          )
        : undefined;
      if (
        staticFilters &&
        (!reconciled ||
          (!reconciled.newDataQueryId &&
            !objectHasEveryEqualPropOfAnother(staticFilters, reconciled.dataQuery.filters)))
      ) {
        throw new Error('Unable to apply required grid filters to the copied preset');
      }
      const dataQueryId = reconciled?.newDataQueryId ?? presetDetail.dataQueryId;
      const dataQuery = reconciled?.newDataQueryId
        ? await httpCalls.getDataQuery(dataQueryId, {forCopy: true})
        : (reconciled?.dataQuery ?? (await httpCalls.getDataQuery(dataQueryId, {forCopy: true})));
      if (!dataQuery) {
        throw new Error('Unable to load the custom preset data query');
      }

      const virtualPreset: VirtualPreset = {
        columnsSettings: filterTechnicalArray(presetDetail.columnsSettings),
        gridSettings: presetDetail.gridSettings,
        dataQueryId,
      };
      await httpCalls.updateVirtualPreset(virtualPreset, {forCopy: true});
      store.setVirtualPreset(virtualPreset);
      await activateVirtualPreset(virtualPreset, dataQuery, true);
    } finally {
      httpCalls.endVirtualPresetCopy();
    }
  };

  const resetOverrides = async (): Promise<void> => {
    if (!isPresetCustom(activePreset)) {
      return;
    }
    await activatePreset(activePreset as Preset);
  };

  return {activatePreset, deactivatePreset, applyPresetToVirtual, resetOverrides};
};
